import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Table, TextLink, Tooltip } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { Trash2Icon, Loader2Icon, EyeIcon } from 'lucide-react';
import type { PlanProduct } from '../types/plan.types';

interface Props {
    data: PlanProduct[];
    onRemove: (productId: string) => void;
    removingProductId?: string | null;
    isLoading?: boolean;
}

export const PlanProductDataTable: React.FC<Props> = ({
    data,
    onRemove,
    removingProductId = null,
    isLoading = false,
}) => {
    const navigate = useNavigate();

    const columns = useMemo<ColumnDef<PlanProduct>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Product Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
                const productId = info.row.original.id;
                const isRemoving = productId === removingProductId;

                if (isRemoving) {
                    return <Loader2Icon size={18} className="animate-spin text-mp-muted" />;
                }

                return (
                    <div className="flex items-center gap-2">
                        <Tooltip content="View product">
                            <TextLink
                                color="primary"
                                onClick={() => navigate(`/smr-admin/products/${productId}`)}
                                ariaLabel="View product"
                                startIcon={<EyeIcon size={20} />}
                            />
                        </Tooltip>
                        <PermissionGuard permission={{ resource: 'smr', action: 'plan.edit' }}>
                            <Tooltip content="Remove product">
                                <TextLink
                                    color="error"
                                    onClick={() => onRemove(productId)}
                                    disabled={removingProductId !== null}
                                    ariaLabel="Remove product"
                                    startIcon={<Trash2Icon size={18} />}
                                />
                            </Tooltip>
                        </PermissionGuard>
                    </div>
                );
            },
        },
    ], [navigate, onRemove, removingProductId]);

    if (isLoading) {
        return <LoadingSpinner size="lg" className="py-8" message="Loading products..." />;
    }

    return <Table<PlanProduct> data={data} columns={columns} enableSorting={false} />;
};
